import type { RefObject } from 'react';

import type { FocusFallbackListener } from '#src/app/shared/hooks/focus';
import { useFocusFallbackRef } from '#src/app/shared/hooks/focus';

const DEVICE_ATTRIBUTE = 'data-see-all-topics';
const SEE_ALL_BUTTON_SELECTOR = `[${DEVICE_ATTRIBUTE}]`;

/**
 * Re-focuses the "see all topics" button for the same device in the subtree that replaced the one
 * holding focus, and does nothing when some other element was removed.
 *
 * The trending section's skeleton renders the real see-all buttons, but each step of the lazy load
 * (client render, scroll trigger, chunk resolution, query resolution) mounts a brand new subtree
 * rather than reusing the DOM, so a button holding focus is destroyed and focus drops to the
 * document.
 *
 * The replacement subtree has no see-all button at all when `TrendingSection` bails out, which it
 * does on a failed fetch and when the section has nothing to show, so fall back to the container
 * to keep the user roughly where they were.
 */
const restoreSeeAllTopicsFocus: FocusFallbackListener = ({
	container,
	focus,
	removedElement,
	removedFocusedElement,
}) => {
	if (!removedElement || !removedFocusedElement) return false;

	const removedButton = removedFocusedElement.closest(SEE_ALL_BUTTON_SELECTOR);
	if (!removedButton || !removedElement.contains(removedButton)) return false;

	const device = removedButton.getAttribute(DEVICE_ATTRIBUTE);
	const replacement = Array.from(container.querySelectorAll(SEE_ALL_BUTTON_SELECTOR)).find(
		(button) => button.getAttribute(DEVICE_ATTRIBUTE) === device,
	);

	return focus(replacement) || focus(container);
};

/**
 * Returns a ref for the element wrapping the lazy-loaded trending section, so focus survives the
 * skeleton being swapped out from under a keyboard user.
 */
export function useTrendingSectionFocusFallbackRef(): RefObject<null> {
	return useFocusFallbackRef(restoreSeeAllTopicsFocus);
}
